// Gráfica pequeña del pulso: una métrica hora a hora, hoy (línea gruesa, solo horas cerradas) frente a
// ayer (línea fina). Arriba, el valor de la última hora cerrada de hoy y cómo va frente a esa misma
// hora de ayer. Las horas se rotulan en UTC.
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { delta, horaUtc, SIN_VALOR, tramosDeHoras, type Delta } from './formato';
import { Etiqueta, FOCO, franjaHora, usePuntero, type FilaEtiqueta } from './Puntero';
import { useTamano } from './useTamano';

const TONO: Record<Delta['tono'], string> = {
  bueno: 'text-oficina-trabajando',
  malo: 'text-oficina-error',
  neutro: 'text-oficina-texto3',
};

/** Por debajo de este ancho (px) los rótulos de 6 en 6 horas se pisan: van de 12 en 12 y sin el pie. */
const ESTRECHA = 300;
const IZQ = 6;
const DER = 6;
const ARRIBA = 6;
const ABAJO = 20;

function puntos(valores: Array<number | null>, x: (h: number) => number, y: (v: number) => number): string[] {
  // Un tramo por cada racha de horas con valor (un hueco no se une con una recta engañosa).
  const tramos: string[] = [];
  let actual: string[] = [];
  valores.forEach((v, h) => {
    if (v === null) {
      if (actual.length) tramos.push(actual.join(' '));
      actual = [];
    } else actual.push(`${x(h + 0.5).toFixed(1)},${y(v).toFixed(1)}`);
  });
  if (actual.length) tramos.push(actual.join(' '));
  return tramos;
}

export function MiniHora({
  titulo,
  hoy,
  ayer,
  formato,
  enPuntos,
  subirEsBueno = true,
  referencia,
  pie,
  paradas,
}: {
  titulo: string;
  hoy: Array<number | null>;
  ayer: Array<number | null>;
  formato: (v: number) => string;
  /** El cambio frente a ayer se da en puntos (porcentajes) o en % de cambio. */
  enPuntos: boolean;
  subirEsBueno?: boolean;
  referencia: Date;
  /** Texto junto al título («última hora cerrada»). 22-10 (revisión): en el Pulso lo dice una sola leyenda para todas. */
  pie?: string;
  /** Horas con Calypso parado: se sombrean (una caída real, no un mal día) y lo dice la etiqueta. */
  paradas?: { hoy: number[]; ayer: number[] };
}) {
  const [ref, tam] = useTamano<HTMLDivElement>(60);
  const ancho = tam?.ancho ?? 320;
  const alto = tam?.alto ?? 90;
  const estrecha = ancho < ESTRECHA;
  const ultima = hoy.reduce<number>((u, v, i) => (v !== null ? i : u), -1);
  const valorHoy = ultima >= 0 ? (hoy[ultima] ?? null) : null;
  const valorAyer = ultima >= 0 ? (ayer[ultima] ?? null) : null;
  const d = delta(valorHoy, valorAyer, enPuntos, subirEsBueno);
  const valores = [...hoy, ...ayer].filter((v): v is number => v !== null);
  // Escala ajustada a los datos (no desde cero): lo que importa aquí es si hoy va por encima o por
  // debajo de ayer y hacia dónde va, y desde cero las líneas salen planas.
  const tope = valores.length ? Math.max(...valores) : 1;
  const suelo = valores.length ? Math.min(...valores) : 0;
  const margen = Math.max((tope - suelo) * 0.15, Math.abs(tope) * 0.02, 1e-9);
  const max = tope + margen;
  const min = Math.max(0, suelo - margen);
  const x = (h: number) => IZQ + ((ancho - IZQ - DER) * h) / 24;
  const y = (v: number) => ARRIBA + (alto - ARRIBA - ABAJO) * (1 - (v - min) / (max - min));
  const hayDato = (h: number) => hoy[h] != null || ayer[h] != null;
  const p = usePuntero({
    n: 24,
    ancho,
    indiceDe: (px) => {
      const h = Math.max(0, Math.min(23, Math.floor(((px - IZQ) / (ancho - IZQ - DER)) * 24)));
      return hayDato(h) ? h : null;
    },
    valido: hayDato,
  });
  const hm = p.i;
  const filas: FilaEtiqueta[] =
    hm === null
      ? []
      : [
          { etiqueta: t('salaControl.hoy'), valor: hoy[hm] == null ? SIN_VALOR : formato(hoy[hm]!), marca: 'bg-oficina-te-necesita' },
          { etiqueta: t('salaControl.ayerLeyenda'), valor: ayer[hm] == null ? SIN_VALOR : formato(ayer[hm]!), marca: 'bg-oficina-texto3', tenue: true },
        ];
  const paradoHoy = new Set(paradas?.hoy ?? []);
  const paradoAyer = new Set(paradas?.ayer ?? []);
  if (hm !== null && (paradoHoy.has(hm) || paradoAyer.has(hm))) {
    filas.push({ etiqueta: t(paradoHoy.has(hm) ? 'salaControl.parado.hoy' : 'salaControl.parado.ayer'), valor: t('salaControl.parado.valor'), marca: 'bg-oficina-error' });
  }
  const tramos = tramosDeHoras([...paradoHoy, ...paradoAyer]);
  const dh = hm === null ? null : delta(hoy[hm] ?? null, ayer[hm] ?? null, enPuntos, subirEsBueno);
  return (
    <div className="flex min-h-0 flex-col rounded-o-m bg-oficina-elevada px-3.5 pb-2 pt-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="truncate text-o-sub text-oficina-texto2">{titulo}</span>
        {estrecha || !pie ? null : <span className="whitespace-nowrap text-o-nota text-oficina-texto3">{pie}</span>}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-o-titulo2 font-semibold tracking-tight">{valorHoy === null ? SIN_VALOR : formato(valorHoy)}</span>
        {d && d.direccion !== 'igual' ? (
          <span className={cn('text-o-nota', TONO[d.tono])}>{`${d.direccion === 'sube' ? '↑' : '↓'} ${d.texto}`}</span>
        ) : null}
      </div>
      <div ref={ref} className="min-h-[60px] flex-1">
        <div {...p.props} className={cn('relative h-full', FOCO)} aria-label={titulo}>
        <svg viewBox={`0 0 ${ancho} ${alto}`} className={tam ? 'block h-full w-full' : 'block h-auto w-full'} role="img" aria-label={titulo}>
          {tramos.map(([a, b]) => (
            <rect key={`p${a}`} x={x(a)} width={x(b) - x(a)} y={ARRIBA} height={alto - ARRIBA - ABAJO} className="fill-oficina-error/10" />
          ))}
          <line x1={IZQ} x2={ancho - DER} y1={alto - ABAJO} y2={alto - ABAJO} className="stroke-oficina-linea" />
          {hm !== null ? <line x1={x(hm + 0.5)} x2={x(hm + 0.5)} y1={ARRIBA} y2={alto - ABAJO} className="stroke-oficina-texto3" /> : null}
          {puntos(ayer, x, y).map((pt) => (
            <polyline key={`a${pt}`} points={pt} fill="none" strokeWidth={1.5} className="stroke-oficina-texto3" />
          ))}
          {puntos(hoy, x, y).map((pt) => (
            <polyline key={`h${pt}`} points={pt} fill="none" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" className="stroke-oficina-te-necesita" />
          ))}
          {/* 22-10 (revisión de la sala): dónde acaba hoy, siempre a la vista (antes solo al pasar el ratón). */}
          {hm === null && valorHoy !== null ? <circle cx={x(ultima + 0.5)} cy={y(valorHoy)} r={3.5} strokeWidth={1.5} className="fill-oficina-te-necesita stroke-oficina-elevada" /> : null}
          {hm !== null && ayer[hm] != null ? <circle cx={x(hm + 0.5)} cy={y(ayer[hm]!)} r={3.5} strokeWidth={1.5} className="fill-oficina-texto3 stroke-oficina-elevada" /> : null}
          {hm !== null && hoy[hm] != null ? <circle cx={x(hm + 0.5)} cy={y(hoy[hm]!)} r={4.5} strokeWidth={2} className="fill-oficina-te-necesita stroke-oficina-elevada" /> : null}
          {(estrecha ? [0, 12, 24] : [0, 6, 12, 18, 24]).map((h) => (
            <text key={h} x={x(h)} y={alto - 5} textAnchor={h === 0 ? 'start' : h === 24 ? 'end' : 'middle'} className="fill-oficina-texto3 text-o-mini">
              {horaUtc(h, referencia)}
            </text>
          ))}
        </svg>
        {hm !== null && p.activa ? (
          <Etiqueta
            x={x(hm + 0.5)}
            ancho={ancho}
            titulo={franjaHora(hm)}
            filas={filas}
            pie={dh && dh.direccion !== 'igual' ? <span className={TONO[dh.tono]}>{`${dh.direccion === 'sube' ? '↑' : '↓'} ${dh.texto} ${t('salaControl.grafica.frenteAyer')}`}</span> : undefined}
          />
        ) : null}
        </div>
      </div>
    </div>
  );
}
